(function () {
  angular
    .module("akitabox.ui.components.buildingInfo")
    .controller("EditBuildingDialogController", EditBuildingDialogController);

  function EditBuildingDialogController(
    // Angular
    $q,
    // Material
    $mdDialog,
    // Third Party
    moment,
    // Constants
    BUILDING_TYPES,
    // Services
    BuildingService,
    MapService,
    ToastService
  ) {
    var self = this;

    // Constants
    var ADDRESS_FIELDS = [
      "address",
      "city",
      "state",
      "postal_code",
      "country",
      "lat",
      "lng",
    ];

    // Private Attributes
    var changes = {}; // pending changes
    var originalDateBuilt = moment(self.building.date_built);
    var originalDatePurchased = moment(self.building.date_purchased);
    var valid = true;
    var fieldValidity = {
      name: true,
      address: true,
      type: true,
      date_built: true,
      date_purchased: true,
      emergency_contact_name: true,
      emergency_contact_phone: true,
      website: true,
      description: true,
    };

    // Attributes
    self.formData = angular.extend({}, self.building);
    self.permissions = self.permissions || {};
    self.place = setupBuildingLocation(self.building);
    self.yearBuilt = originalDateBuilt.year();
    self.yearPurchased = originalDatePurchased.year();
    self.BUILDING_TYPES = BUILDING_TYPES;
    self.year = moment().year();

    // Functions
    self.onChange = onChange;
    self.cancel = $mdDialog.cancel;
    self.save = save;
    self.isDisabled = isDisabled;

    // =================
    // Private Functions
    // =================

    /**
     * Setup building location object to be used by the address input
     *
     * @param {Building} building A building
     */
    function setupBuildingLocation(building) {
      return {
        address: building.address,
        city: building.city,
        state: building.state,
        postal_code: building.postal_code,
        country: building.country,
        description: MapService.formatDescription(building),
      };
    }

    /**
     * Get generic updates fields from another object like pending changes
     *
     * @param {Object} src  Source object
     */
    function getGenericUpdateFields(src) {
      var data = {};
      var nonGenericFields = ["name"];
      Array.prototype.push.apply(nonGenericFields, ADDRESS_FIELDS);
      var srcFields = Object.keys(src);
      for (var f in srcFields) {
        var field = srcFields[f];
        if (nonGenericFields.indexOf(field) < 0) {
          data[field] = src[field];
        }
      }
      return data;
    }

    /**
     * Remove fields from changes
     *
     * @param {String[]} fields List of fields to remove
     */
    function removeChanges(fields) {
      for (var k in fields) {
        var key = fields[k];
        delete changes[key];
      }
    }

    /**
     * Update particular building fields
     *
     * @param {String[]}  fields  List of field to update
     * @param {Object}    updates Object containing new data
     */
    function updateBuildingFields(fields, updates) {
      for (var i in fields) {
        var field = fields[i];
        var update = updates[field];
        self.building[field] = update;
        if (field === "lat") {
          self.building.coordinates.latitude = update;
        } else if (field === "lng") {
          self.building.coordinates.longitude = update;
        }
      }
    }

    /**
     * Update the building's name
     *
     * @param {Object} data New building data
     */
    function updateName(data) {
      var fields = ["name"];
      return BuildingService.changeName(self.building._id, { name: data.name })
        .then(function (building) {
          removeChanges(fields);
          updateBuildingFields(fields, building);
          return self.building;
        })
        .catch(function (err) {
          ToastService.showError("Unable to update building name");
          return $q.reject(err);
        });
    }

    /**
     * Update the building's address
     *
     * @param {Object} data New building data
     */
    function updateAddress(data) {
      // Remove non-address fields
      var addressData = {};
      for (var k in ADDRESS_FIELDS) {
        var field = ADDRESS_FIELDS[k];
        addressData[field] = data[field];
      }
      return BuildingService.update(self.building._id, addressData)
        .then(function (building) {
          removeChanges(ADDRESS_FIELDS);
          updateBuildingFields(ADDRESS_FIELDS, building);
          return self.building;
        })
        .catch(function (err) {
          ToastService.showError("Unable to update building address");
          return $q.reject(err);
        });
    }

    /**
     * Generic building update
     *
     * @param {Object} data New building data
     */
    function update(data) {
      var fields = Object.keys(data);
      return BuildingService.update(self.building._id, data)
        .then(function (building) {
          removeChanges(fields);
          updateBuildingFields(fields, building);
          return self.building;
        })
        .catch(function (err) {
          ToastService.showError("Unable to update building");
          return $q.reject(err);
        });
    }

    /**
     * Parse year string into date (moment)
     *
     * @param {Number} value Year
     *
     * @returns {Moment} Moment
     */
    function parseYear(value) {
      if (!value) {
        return null;
      } else {
        return moment("" + value + "-01-02");
      }
    }

    // =================
    // Public Functions
    // =================

    /**
     * Handle field change
     *
     * @param {Object} $event Change event
     * @param {String} type Type of update (e.g name, address)
     */
    function onChange($event, type) {
      var data = {};
      fieldValidity[type] = !$event.invalid;
      switch (type) {
        case "name":
          data.name = $event.model || null;
          break;
        case "address":
          var model = $event.model;
          if (model) {
            data = model; // contains all address fields
            // Remove description, not schema field
            if (data && data.description) {
              delete data.description;
            }
          } else {
            data.address = null;
            data.city = null;
            data.coordinates = null;
            data.country = null;
            data.lat = null;
            data.lng = null;
            data.state = null;
          }
          break;
        case "date_built":
          var dateBuilt = parseYear($event.model);
          if (dateBuilt) {
            self.yearBuilt = dateBuilt.year();
            if (self.yearBuilt === originalDateBuilt.year()) {
              delete changes.date_built;
            } else {
              data.date_built = dateBuilt.valueOf();
            }
          } else {
            self.yearBuilt = null;
            data.date_built = null;
          }
          break;
        case "date_purchased":
          var datePurchased = parseYear($event.model);
          if (datePurchased) {
            self.yearPurchased = datePurchased.year();
            if (self.yearPurchased === originalDatePurchased.year()) {
              delete changes.date_purchased;
            } else {
              data.date_purchased = datePurchased.valueOf();
            }
          } else {
            self.yearPurchased = null;
            data.date_purchased = null;
          }
          break;
        default:
          data[type] = $event.model || null;
          break;
      }

      angular.extend(self.formData, data);

      // Remove unchanged values
      var newChanges = angular.extend({}, changes, data);
      changes = {};
      for (var i in newChanges) {
        var value = newChanges[i];
        var original = self.building[i];
        if (value !== original) {
          changes[i] = newChanges[i];
        }
      }

      // Calculate form validity
      var invalidCount = Object.keys(fieldValidity).reduce(function (
        count,
        key
      ) {
        var valid = fieldValidity[key];
        return count + (valid ? 0 : 1);
      },
      0);
      var datesValid = true;
      if (self.yearBuilt && self.yearPurchased) {
        datesValid = self.yearBuilt <= self.yearPurchased;
      }
      valid = invalidCount === 0 && datesValid;
    }

    /**
     * Save the proposed building updates (changes)
     */
    function save() {
      self.saving = true;
      var updates = [];

      // Name update
      if (changes.name) {
        updates.push(updateName(changes));
      }
      // If the location has changed, the address has been updated
      if (changes.lat) {
        updates.push(updateAddress(changes));
      }
      // Generic update
      var data = getGenericUpdateFields(changes);
      if (Object.keys(data).length) {
        updates.push(update(data));
      }

      if (updates.length) {
        return $q
          .all(updates)
          .then(function () {
            $mdDialog.hide(self.building);
          })
          .finally(function () {
            self.saving = false;
          });
      } else {
        self.saving = false;
      }
    }

    function isDisabled() {
      var noChanges = angular.isEmpty(changes, false);
      return noChanges || self.saving || !valid;
    }
  }
})();
